import { useState, useEffect, useMemo } from "react";
import type { InvestorProfile, Structure } from "../../types/entities";
import { supabase, isSupabaseEnabled } from "../../lib/supabase";
import type { PropertyRecord } from "../../types/portfolio";
import type { BudgetCategory, BudgetSettings, BudgetBucket } from "../../types/budget";
import { detectPortfolioPhase, detectCurrentConstraint, deductibleInterest, interEntityLoanInterest, calculateMarginalRate, personalTaxSavingFromLoss, estimateIncomeTax, calculateBorrowingCostDeduction, filterDeductibleExpenseRows, annualLoanInterest, annualLoanRepayment, isSMSF, propertyMetrics, portfolioSummary, lvr, isPersonalProperty, formatRateComponentsLabel, deriveFamilyMlsContext } from "../../lib/calculations";
import { computeLensAllocatedLoss, resolveLensProfile, householdFactorOf } from "../../lib/householdScope";
import { computeNoticeBoard } from "../../lib/computeNoticeBoard";
import { NoticeTier } from "./NoticeItem";
import { EmailComposeModal } from "../ui/EmailComposeModal";
import type { NoticeBoardItem } from "../../types/noticeboard";
import { loadAppSettings } from "../settings/SettingsPage";
import { InfoTooltip } from "../ui/InfoTooltip";
import type { HtagSummary } from "../../lib/htag-types";
import { getHtagType } from "../../lib/htag-property-type";

// ── helpers ────────────────────────────────────────────────────────────────

function currency(n: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(n);
}

function toAnnualIncome(prop: PropertyRecord): number {
  if (prop.incomeRows && prop.incomeRows.length > 0) {
    const m: Record<string, number> = {
      weekly: 52,
      fortnightly: 26,
      monthly: 12,
      quarterly: 4,
      yearly: 1,
      annual: 1,
    };
    return prop.incomeRows.reduce(
      (sum, r) => sum + r.amount * (m[r.frequency] ?? 1),
      0
    );
  }
  return (prop.weeklyRent ?? 0) * 52;
}

function toAnnualExpenses(prop: PropertyRecord): number {
  if (prop.expenseRows && prop.expenseRows.length > 0) {
    const m: Record<string, number> = {
      weekly: 52,
      fortnightly: 26,
      monthly: 12,
      quarterly: 4,
      yearly: 1,
      annual: 1,
    };
    // Exclude mortgage repayments — interest is computed separately. See
    // calculations.ts EXCLUDED_EXPENSE_CATEGORIES.
    return filterDeductibleExpenseRows(prop.expenseRows).reduce(
      (sum, r) => sum + r.amount * (m[r.frequency] ?? 1),
      0
    );
  }
  return (
    (prop.annualRates ?? 0) +
    (prop.annualInsurance ?? 0) +
    (prop.annualStrata ?? 0) +
    (prop.annualLandTax ?? 0) +
    (prop.annualMaintenance ?? 0) +
    (prop.annualPropertyManagement ?? 0) +
    (prop.monthlyExpenses ?? 0) * 12
  );
}

const TRUST_TYPES = [
  "Trust",
  "Company / Trust",
  "Discretionary trust",
  "Discretionary trust with corporate trustee",
  "Unit trust",
  "Bare trust",
];

function isTrustStructure(s: Structure): boolean {
  return TRUST_TYPES.some(
    (t) => s.entityType === t || s.entityType?.includes(t)
  );
}

function isCompanyEntity(entityType: string | undefined): boolean {
  if (!entityType) return false;
  return (entityType.includes("Company") || entityType.includes("Pty Ltd")) && !TRUST_TYPES.includes(entityType);
}

// ── sub-components ─────────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  sub,
  valueClass,
  tip,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClass?: string;
  tip?: string;
}) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm border-t-2 border-t-[#C47A2F]">
      <div className="flex items-center gap-1 text-xs font-medium tracking-wide uppercase text-neutral-500 mb-1.5">
        {label}
        {tip && <InfoTooltip tip={tip} />}
      </div>
      <div className={`text-lg font-semibold ${valueClass ?? "text-neutral-900"}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-neutral-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// ── props ──────────────────────────────────────────────────────────────────

interface DashboardPageProps {
  profile: InvestorProfile;
  portfolioProperties: PropertyRecord[];
  portfolioStructures: Structure[];
  budgetSettings: BudgetSettings;
  budgetCategories: BudgetCategory[];
  onNavigate: (page: string) => void;
  onSelectProperty?: (id: string) => void;
  setupComplete?: { profile: boolean; portfolio: boolean; loans: boolean; budget: boolean; goals: boolean };
  subscriptionTier?: string;
  userId?: string;
  appSettings: import("../settings/SettingsPage").AppSettings;
}

// ── component ──────────────────────────────────────────────────────────────

export default function DashboardPage({
  profile,
  portfolioProperties,
  portfolioStructures,
  budgetSettings,
  budgetCategories,
  onNavigate,
  onSelectProperty,
  setupComplete,
  subscriptionTier,
  userId,
  appSettings,
}: DashboardPageProps) {
  const [setupDismissed, setSetupDismissed] = useState(
    () => localStorage.getItem("ppa:setup-dismissed") === "true"
  );

  // ── Referral widget state ──────────────────────────────────────────────────
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referralStats, setReferralStats] = useState({ total: 0, subscribed: 0 });
  const [emailItem, setEmailItem] = useState<NoticeBoardItem | null>(null);
  // B27 #5 — Net Cashflow KPI toggle. Three states:
  //   'aftertax' — cash position + expected ATO refund on deductible loss
  //                (default — most useful single number for typical investor)
  //   'cash'     — full P&I subtracted (real bank outflow, pre-refund)
  //   'tax'      — interest only (the deductible-loss line accountant sees)
  const [cashflowMode, setCashflowMode] = useState<"aftertax" | "cash" | "tax">("aftertax");
  const [referralDismissed, setReferralDismissed] = useState(
    () => localStorage.getItem("ausprop_referral_dismissed") === "true"
  );
  const [referralCopied, setReferralCopied] = useState(false);

  const [htagCache, setHtagCache] = useState<Map<string, HtagSummary>>(new Map());

  useEffect(() => {
    if (!userId || subscriptionTier !== "investor" || referralDismissed) return;
    if (!isSupabaseEnabled() || !supabase) return;

    async function fetchReferralData() {
      if (!supabase || !userId) return;
      const { data: profileRow } = await supabase
        .from("user_profiles")
        .select("referral_code")
        .eq("id", userId)
        .maybeSingle();
      if (profileRow?.referral_code) setReferralCode(profileRow.referral_code as string);

      const { data: rows } = await supabase
        .from("referrals")
        .select("status")
        .eq("referrer_user_id", userId);
      if (rows) {
        setReferralStats({
          total: rows.length,
          subscribed: rows.filter((r) => r.status === "subscribed" || r.status === "rewarded").length,
        });
      }
    }
    void fetchReferralData();
  }, [userId, subscriptionTier, referralDismissed]);

  // ── active properties ──────────────────────────────────────────────────
  const activeProperties = portfolioProperties.filter(
    (p) => !p.notes?.includes("[DRAFT SCENARIO]")
  );

  // ── HTAG cache for market signals in properties table ──────────────────────
  // Stable string key derived from the set of LGA IDs — only changes when
  // properties are added/removed, not on every render.
  const lgaIdsKey = activeProperties
    .filter(p => p.lgaId)
    .map(p => `${p.lgaId}:${getHtagType(p.propertyType ?? p.assetClass ?? "") ?? "house"}`)
    .sort()
    .join(",");

  useEffect(() => {
    if (!isSupabaseEnabled() || !supabase || !lgaIdsKey) return;
    const uniqueLgaIds = [...new Set(
      activeProperties.filter(p => p.lgaId).map(p => p.lgaId!)
    )];
    if (uniqueLgaIds.length === 0) return;
    supabase
      .from("htag_cache")
      .select("lga_id, property_type, data")
      .in("lga_id", uniqueLgaIds)
      .then(({ data }) => {
        const map = new Map<string, HtagSummary>();
        (data ?? []).forEach((row: { lga_id: string; property_type: string; data: unknown }) => {
          map.set(`${row.lga_id}:${row.property_type}`, row.data as HtagSummary);
        });
        setHtagCache(map);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lgaIdsKey]);

  // ── portfolio metrics ──────────────────────────────────────────────────
  // All aggregates are SHARE-BASED (Day 36 — co-ownership). Per-property
  // display lines below still show gross. See ARCHITECTURE.md §7.
  // SMSF properties are included in value/debt/equity (they're part of
  // the user's wealth) but excluded from the personal cashflow / top-up
  // KPIs (SMSF is funded by contributions, not personal cashflow).
  const ownershipFactorOf = (p: PropertyRecord) => (p.ownershipPercent ?? 100) / 100;
  // B110 — household-scope factor closure. Returns 1.0 for household-shared
  // properties (JT, TIC w/ spouse, sole-but-cohabiting), ownership share
  // otherwise. Used by the multi-adult cashflow re-aggregation below; falls
  // back to ownershipFactorOf when no profile is loaded so the closure is
  // always callable.
  const structureFor = (p: PropertyRecord) =>
    portfolioStructures.find((s) => s.id === p.structureId);
  const householdFactorFn = (p: PropertyRecord) =>
    profile ? householdFactorOf(p, structureFor(p), profile) : ownershipFactorOf(p);
  const isSMSFStructure = (structureId: string) =>
    portfolioStructures.some((s) => s.id === structureId && isSMSF(s.entityType));
  const smsfActive = activeProperties.filter((p) => isSMSFStructure(p.structureId));
  const includeSMSF = appSettings.includeSMSFInPortfolio;
  // When toggle is ON, use all properties for personal KPIs. When OFF, exclude SMSF.
  const nonSmsfActive = includeSMSF ? activeProperties : activeProperties.filter((p) => !isSMSFStructure(p.structureId));
  // When toggle is OFF, value/debt/equity also exclude SMSF.
  const valueScope = includeSMSF ? activeProperties : nonSmsfActive;
  // B27 #1 + #4: portfolio totals route through portfolioSummary() SSOT.
  // Value is always gross (property is worth full market value regardless of
  // ownership split). Debt is always gross (the bank holds the full loan).
  // Portfolio LVR is invariant to ownership — the factor cancels in the ratio.
  const portfolioTotals = portfolioSummary(valueScope);
  const totalValue = portfolioTotals.totalValue;
  const totalDebt = portfolioTotals.totalDebt;
  const totalEquity = totalValue - totalDebt;
  const portfolioLVR = lvr(totalDebt, totalValue);

  // Personal cashflow KPIs — SMSF excluded. SMSF is funded by contributions.
  const totalAnnualRent = nonSmsfActive.reduce(
    (sum, p) => sum + toAnnualIncome(p) * ownershipFactorOf(p),
    0
  );
  const portfolioGrossYield =
    totalAnnualRent > 0 && totalValue > 0
      ? (totalAnnualRent / totalValue) * 100
      : 0;
  const totalAnnualExpenses = nonSmsfActive.reduce(
    (sum, p) => sum + toAnnualExpenses(p) * ownershipFactorOf(p),
    0
  );
  const totalAnnualInterest = nonSmsfActive.reduce(
    (sum, p) => sum + annualLoanInterest(p) * ownershipFactorOf(p),
    0
  );
  // Tax-position cashflow (rent − opex − interest, share-aware) — what the
  // accountant sees on the deductible-loss line. Excludes principal repayments
  // because principal isn't deductible. Used for tax narratives + AI context.
  const taxAnnualCashflow =
    totalAnnualRent - totalAnnualExpenses - totalAnnualInterest;
  // Cash-position cashflow (rent − opex − full P&I repayment, share-aware) —
  // what actually leaves the bank. Routed through the SSOT `portfolioSummary`
  // helper which sums `propertyMetrics(p).netCashflowShare` (B27 #5 fix).
  // monthlyShortfall reflects real-money shortfall so feeds Notice Board
  // alerts correctly; tax-position shortfall would understate it.
  const cashAnnualCashflow = portfolioSummary(nonSmsfActive).totalCashflow * 12;
  const monthlyShortfall =
    cashAnnualCashflow < 0 ? Math.abs(cashAnnualCashflow) / 12 : 0;

  // ── B110 household-scope cashflow variants (multi-adult only) ──────────
  // Mirrors the dual-display pattern from B103 Phase 4c-1: keep the existing
  // single-lens derivations (used by single-adult households for back-compat)
  // and compute parallel household-scope figures for multi-adult. Selection
  // happens below once `adults` is derived. Single-adult path is bit-identical
  // to pre-B110 because `householdFactorFn(p) === ownershipFactorOf(p)` when
  // there's no co-resident partner.
  const taxAnnualCashflowHousehold = nonSmsfActive.reduce((sum, p) => {
    const f = householdFactorFn(p);
    return sum + (toAnnualIncome(p) * f) - (toAnnualExpenses(p) * f) - (annualLoanInterest(p) * f);
  }, 0);
  const cashAnnualCashflowHousehold = portfolioSummary(nonSmsfActive, {
    scope: "household",
    householdFactorFn,
  }).totalCashflow * 12;

  // ── tax position ───────────────────────────────────────────────────────
  const salaryIncome = profile?.annualPreTaxIncome ?? 0;
  const hasIncome = salaryIncome > 0;
  // Nominal bracket MTR retained for display contexts ("at X% marginal rate").
  // Tax-saving math uses personalTaxSavingFromLoss → effectiveMarginalRate
  // (bracket + Medicare + MLS + HELP). See ARCHITECTURE §7 + L35.
  const marginalRate = profile?.marginalTaxRate ?? calculateMarginalRate(salaryIncome);

  // Personal-name properties only — trust/company losses are quarantined.
  // B106: structureId IS NULL is the SSOT for personal-name. Post-migration
  // there are no Personal-typed structures (DB CHECK rejects them).
  const personalProps = activeProperties.filter((p) => isPersonalProperty(p));

  const today = new Date();
  // Current FY start: 1 Jul of the current financial year
  const currentFyStartYear = today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1;
  const currentFyStartDate = new Date(currentFyStartYear, 6, 1);

  const personalNetRental = personalProps.reduce((sum, p) => {
    const factor = ownershipFactorOf(p);
    const rent = toAnnualIncome(p) * factor;
    const expenses = toAnnualExpenses(p) * factor;
    const interest = annualLoanInterest(p) * factor;
    const borrowingDeduction = calculateBorrowingCostDeduction(p, currentFyStartDate) * factor;
    return sum + (rent - expenses - interest - borrowingDeduction);
  }, 0);

  const negGearingLoss = personalNetRental < 0 ? Math.abs(personalNetRental) : 0;
  const taxSaving = profile ? personalTaxSavingFromLoss(negGearingLoss, profile) : 0;
  const isPositivelyGeared = personalNetRental >= 0 && personalProps.length > 0;

  // B103 Phase 4c-1 — Adults pool for the dual tax-saving cards.
  // Mirrors TaxPage Phase 4b derivation (primary + non-dependent partners
  // from householdMembers). Single-adult households render the existing
  // single card unchanged; multi-adult households render a stacked layout
  // showing the combined household total + per-adult sub-cards.
  const adults = useMemo(() => {
    const primaryAdult = {
      id: "primary" as const,
      label: profile?.preferredName ?? profile?.fullLegalName ?? "You",
    };
    // Issue 3 (Day 52) — coerce missing memberType to partner. There are
    // only two member kinds (partner / dependent); anything else is a
    // partner by definition. Mirrors the householdScope filter pattern
    // and prevents the same regression if a future code path inserts a
    // member without an explicit memberType.
    const partnerAdults = (profile?.householdMembers ?? [])
      .filter((m) => m.memberType !== "dependent")
      .map((m) => ({
        id: m.id,
        label: m.fullLegalName || m.preferredName || "Partner",
      }));
    return [primaryAdult, ...partnerAdults];
  }, [profile?.preferredName, profile?.fullLegalName, profile?.householdMembers]);

  // Per-adult tax data — only computed when adults.length > 1. For single
  // adult, this stays empty and the existing single card renders unchanged.
  const perAdultTax = useMemo(() => {
    if (!profile || adults.length <= 1) return [];
    return adults.map((adult) => {
      const allocatedLoss = computeLensAllocatedLoss(
        activeProperties,
        portfolioStructures,
        profile,
        adult.id,
      );
      const lensProfile = resolveLensProfile(profile, adult.id);
      const adultTaxSaving = personalTaxSavingFromLoss(allocatedLoss, lensProfile);
      // Bracket-MTR label, mirrors the :650 pattern (math is effective
      // marginal via personalTaxSavingFromLoss; label is bracket only).
      const bracketRate = lensProfile.marginalTaxRate
        ?? calculateMarginalRate(lensProfile.annualPreTaxIncome ?? 0);
      // B109 — per-adult rate-components suffix. Empty when no extra
      // components apply (e.g. Medicare-exempt + no HECS). Used by the
      // tooltip below so the label matches the underlying math.
      // B116 — pass family-MLS context so the suffix reflects family-tier
      // MLS when the household has a partner.
      const componentsLabel = formatRateComponentsLabel(
        lensProfile.annualPreTaxIncome ?? 0,
        {
          medicareExempt: lensProfile.medicareExempt,
          medicareReduction: lensProfile.medicareReduction,
          hasPrivateHospitalCover: lensProfile.hasPrivateHospitalCover,
          medicareLevy: lensProfile.medicareLevy,
          hasHECS: lensProfile.hasHECS,
          ...deriveFamilyMlsContext(profile),
        },
      );
      return {
        id: adult.id,
        label: adult.label,
        allocatedLoss,
        taxSaving: adultTaxSaving,
        bracketRate,
        componentsLabel,
      };
    });
  }, [adults, profile, activeProperties, portfolioStructures]);

  // B103 Phase 4c-1 fix (P0): household total must be the sum of per-adult
  // allocations. The legacy `taxSaving` (line 320) is computed from
  // `personalNetRental` using the primary's `ownershipFactor` only — that's
  // the right SSOT for single-adult households (back-compat) but understates
  // the household for multi-adult cases where the partner's allocated share
  // exists outside primary's ownershipPercent. Sum-of-sub-cards is the only
  // value that agrees with what the per-adult cards display.
  const householdTotalTaxSaving = useMemo(
    () => perAdultTax.reduce((s, a) => s + a.taxSaving, 0),
    [perAdultTax],
  );
  // Sum of per-adult allocated losses — used to gate the multi-adult
  // headline state ("show value" vs "Positively geared" vs "—"). Distinct
  // from `negGearingLoss` (line 319) which is primary-lens only.
  const householdAllocatedLoss = useMemo(
    () => perAdultTax.reduce((s, a) => s + a.allocatedLoss, 0),
    [perAdultTax],
  );

  // B110 — Single switch for the Net Cashflow KPI + Notice Board alerts.
  // Multi-adult households see household-scope cashflow (your share + partner's
  // share combined); single-adult households see the legacy single-lens figure
  // and behave bit-identically to pre-B110.
  const isMultiAdult = adults.length > 1;
  const displayCashAnnual = isMultiAdult ? cashAnnualCashflowHousehold : cashAnnualCashflow;
  const displayTaxAnnual  = isMultiAdult ? taxAnnualCashflowHousehold  : taxAnnualCashflow;
  const displayTaxSaving  = isMultiAdult ? householdTotalTaxSaving     : taxSaving;
  // Notice Board alerts must agree with the visible KPI — flip with the
  // displayed cash figure. Single-adult: identical to monthlyShortfall.
  const displayMonthlyShortfall = displayCashAnnual < 0 ? Math.abs(displayCashAnnual) / 12 : 0;

  const taxableIncome = Math.max(0, salaryIncome + personalNetRental);
  const estimatedTaxWithProperty = estimateIncomeTax(taxableIncome).netTax;
  const estimatedTaxWithoutProperty = estimateIncomeTax(salaryIncome).netTax;
  const taxReduction = estimatedTaxWithoutProperty - estimatedTaxWithProperty;
  const paygWithheld = profile?.paygWithheld ?? 0;
  const netOwing = estimatedTaxWithProperty - paygWithheld;

  // ── greeting ───────────────────────────────────────────────────────────
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName =
    profile?.preferredName?.trim() ||
    profile?.fullLegalName?.trim().split(" ")[0] ||
    null;

  const dateStr = today.toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  // ── notice board (replaces old Priority Actions — N5 Stage 2) ──────────
  const usableEquity = Math.max(0, totalValue * 0.8 - totalDebt);
  const noticeBoard = computeNoticeBoard(activeProperties, portfolioStructures, appSettings, {
    lvrPct: portfolioLVR, usableEquity, monthlyShortfall: displayMonthlyShortfall,
  });
  const redItems = noticeBoard.filter((i) => i.priority === "red");
  const amberItems = noticeBoard.filter((i) => i.priority === "amber");
  const greenItems = noticeBoard.filter((i) => i.priority === "green");
  const neutralItems = noticeBoard.filter((i) => i.priority === "neutral");

  // ── lifecycle engine ───────────────────────────────────────────────────
  const maxLVR = loadAppSettings().lvrStandard ?? 80;
  const portfolioPhase = detectPortfolioPhase(activeProperties, [], profile);
  const currentConstraint = detectCurrentConstraint(activeProperties, [], profile, maxLVR);

  // ── goal progress ──────────────────────────────────────────────────────
  const passiveTarget = profile?.passiveIncomeTarget ?? 0;
  // Phase detection uses tax-position cashflow (existing behaviour preserved).
  const currentNet = taxAnnualCashflow;
  const passivePct =
    passiveTarget > 0
      ? Math.min(100, Math.max(0, (currentNet / passiveTarget) * 100))
      : 0;

  // Estimate years to passive target via rent growth
  let yearsToTarget: number | null = null;
  if (passiveTarget > 0 && currentNet < passiveTarget) {
    let rent = totalAnnualRent;
    let expenses = totalAnnualExpenses;
    const interest = totalAnnualInterest;
    for (let yr = 1; yr <= 30; yr++) {
      rent *= 1.04;
      expenses *= 1.028;
      if (rent - expenses - interest >= passiveTarget) {
        yearsToTarget = yr;
        break;
      }
    }
  } else if (passiveTarget > 0 && currentNet >= passiveTarget) {
    yearsToTarget = 0;
  }

  // ── personal finances ──────────────────────────────────────────────────
  const [showFinances, setShowFinances] = useState(false);

  const hasBudgetData =
    budgetCategories.length > 0 ||
    (budgetSettings?.incomeRows?.length ?? 0) > 0;

  // Normalise budget frequencies to monthly
  function budgetToMonthly(amount: number, freq: string): number {
    if (freq === "W" || freq === "Weekly")                       return (amount * 52) / 12;
    if (freq === "F" || freq === "Fortnightly")                  return (amount * 26) / 12;
    if (freq === "Q" || freq === "Quarterly")                    return amount / 3;
    if (freq === "Y" || freq === "Annual" || freq === "Yearly")  return amount / 12;
    return amount; // monthly
  }

  const monthlyIncome = (budgetSettings?.incomeRows ?? []).reduce(
    (sum, r) => sum + budgetToMonthly(r.amount ?? 0, r.frequency ?? "M"),
    0
  );

  const monthlyExpenses = budgetCategories.reduce(
    (sum, cat) => sum + budgetToMonthly(cat.budget ?? 0, cat.frequency ?? "M"),
    0
  );

  const netPersonalPosition = monthlyIncome - monthlyExpenses - monthlyShortfall;

  const totalActualExpenses = budgetCategories.reduce((s: number, c: BudgetCategory) => s + (c.actual ?? 0), 0);
  const totalBudgetExpenses = monthlyExpenses; // already computed above
  const hasActuals = totalActualExpenses > 0;
  const varianceAmt = Math.abs(totalActualExpenses - totalBudgetExpenses);
  const isOverBudget = totalActualExpenses > totalBudgetExpenses;

  const buckets: BudgetBucket[] = budgetSettings?.buckets ?? [];
  const totalBucketAllocation = buckets.reduce((sum, b) => sum + (b.monthlyAmount ?? 0), 0);
  const personalSurplus = monthlyIncome - monthlyExpenses; // before property top-up
  const overAllocated = totalBucketAllocation > personalSurplus && personalSurplus > 0;

  // ── sorted property list ───────────────────────────────────────────────
  const sortedProperties = [...activeProperties].sort(
    (a, b) => (b.currentValue ?? 0) - (a.currentValue ?? 0)
  );

  // ── entity performance ─────────────────────────────────────────────────
  const entityGroups: Array<{
    id: string;
    name: string;
    type: string;
    cashflow: number;
    count: number;
  }> = portfolioStructures.map((s) => {
    const props = activeProperties.filter((p) => p.structureId === s.id);
    const isPersonal = !isCompanyEntity(s.entityType) && !isTrustStructure(s);
    const cashflow = props.reduce((sum, p) => {
      const income = toAnnualIncome(p);
      const expenses = toAnnualExpenses(p);
      const interest = isPersonal
        ? deductibleInterest(p)
        : annualLoanRepayment(p).total;
      const raw = income - expenses - interest;
      return sum + (isCompanyEntity(s.entityType) && raw > 0 ? raw * 0.7 : raw);
    }, 0) - interEntityLoanInterest(activeProperties, s.id);
    return {
      id: s.id,
      name: s.displayName,
      type: s.entityType,
      cashflow,
      count: props.length,
    };
  });

  const unassignedProps = activeProperties.filter((p) => !p.structureId);
  const unassignedCashflow = unassignedProps.reduce((sum, p) => {
    const income = toAnnualIncome(p);
    const expenses = toAnnualExpenses(p);
    const interest = deductibleInterest(p); // unassigned = personal, apply deductible interest
    return sum + income - expenses - interest;
  }, 0);

  // ── page label helper ──────────────────────────────────────────────────
  // ── render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            {firstName ? `${greeting}, ${firstName}.` : `${greeting}.`}
          </h1>
          <p className="text-sm text-neutral-500 mt-0.5">{dateStr}</p>
          {activeProperties.length > 0 && (
            <p className="text-sm text-neutral-600 mt-1">
              Your portfolio of {activeProperties.length}{" "}
              {activeProperties.length === 1 ? "property" : "properties"} is
              worth {currency(totalValue)} with {currency(totalEquity)} equity at{" "}
              {portfolioLVR.toFixed(1)}% LVR.
            </p>
          )}
          {activeProperties.length > 0 && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {/* Phase badge */}
              <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ${
                portfolioPhase === "seed"         ? "bg-neutral-100 text-neutral-600" :
                portfolioPhase === "accumulation" ? "bg-blue-100 text-blue-700" :
                portfolioPhase === "consolidation"? "bg-amber-100 text-amber-700" :
                portfolioPhase === "transition"   ? "bg-purple-100 text-purple-700" :
                                                    "bg-emerald-100 text-emerald-700"
              }`}>
                {portfolioPhase.charAt(0).toUpperCase() + portfolioPhase.slice(1)} Phase
              </span>
              {/* Constraint badge */}
              <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ${
                currentConstraint === "cashflow" ? "bg-red-100 text-red-700" :
                currentConstraint === "capital"  ? "bg-orange-100 text-orange-700" :
                currentConstraint === "both"     ? "bg-red-100 text-red-700" :
                                                   "bg-emerald-100 text-emerald-700"
              }`}>
                {currentConstraint === "cashflow" ? "Cashflow constrained" :
                 currentConstraint === "capital"  ? "Capital constrained" :
                 currentConstraint === "both"     ? "Cashflow & capital constrained" :
                                                   "Ready to acquire"}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Metric Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard
          label="Portfolio Value"
          value={currency(totalValue)}
          tip="Total current market value of all properties across all entities, based on values you've entered."
        />
        <MetricCard
          label="Total Debt"
          value={currency(totalDebt)}
          tip="Sum of all outstanding loan balances across all properties and entities."
        />
        <MetricCard
          label="Equity"
          value={currency(totalEquity)}
          valueClass="text-emerald-700"
          tip="Portfolio Value minus Total Debt. This is your net wealth in property."
        />
        <MetricCard
          label="LVR"
          value={`${portfolioLVR.toFixed(1)}%`}
          valueClass={
            portfolioLVR > 80
              ? "text-red-600"
              : portfolioLVR > 70
              ? "text-amber-600"
              : "text-neutral-900"
          }
          tip="Loan-to-Value Ratio. Total debt divided by total portfolio value. Lower is better. Most lenders prefer below 80%."
        />
        {(() => {
          // After-tax adjustment routes through `displayTaxSaving` — sum of
          // per-adult allocations for multi-adult households (B110), legacy
          // single-lens `taxSaving` for single-adult. Both already exclude
          // SMSF / Trust / Company losses (quarantined per L39); single-adult
          // case is bit-identical to pre-B110 behaviour.
          const afterTaxAnnualCashflow = displayCashAnnual + displayTaxSaving;
          const displayAnnual =
            cashflowMode === "aftertax" ? afterTaxAnnualCashflow
            : cashflowMode === "cash" ? displayCashAnnual
            : displayTaxAnnual;
          const householdNote = isMultiAdult
            ? " Combined household total — your share plus partner's share."
            : "";
          const cashflowTip = (
            cashflowMode === "aftertax"
              ? "Net cash position after tax refund. What salary needs to fund after the ATO refund eventually lands. Cash position plus expected tax refund. If you don't have a PAYG variation you're funding the full Gross position out-of-pocket until tax time."
              : cashflowMode === "cash"
              ? "Gross cash position. What actually leaves your bank account each month — full mortgage repayments (interest + principal) minus rent received. Pre-refund."
              : "Tax-deductible loss. Rent minus expenses minus interest only — principal repayments aren't deductible. Use this to estimate negative gearing benefit."
          ) + householdNote;
          return (
            <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm border-t-2 border-t-[#C47A2F]">
              <div className="flex items-center gap-1 text-xs font-medium tracking-wide uppercase text-neutral-500">
                Net Cashflow
                <InfoTooltip tip={cashflowTip} />
              </div>
              {/* Toggle pills sit on their own line below the label so they're
                  visible at any KPI card width (B27-followup-6: pills were
                  clipped on the 8-col grid when sharing the label flex row). */}
              <div className="mt-1 flex w-fit rounded-full bg-neutral-100 p-0.5 text-[10px]">
                <button
                  onClick={() => setCashflowMode("aftertax")}
                  className={`px-1.5 py-0.5 rounded-full transition ${cashflowMode === "aftertax" ? "bg-white text-neutral-700 shadow-sm" : "text-neutral-400 hover:text-neutral-600"}`}
                  aria-pressed={cashflowMode === "aftertax"}
                >
                  Net
                </button>
                <button
                  onClick={() => setCashflowMode("cash")}
                  className={`px-1.5 py-0.5 rounded-full transition ${cashflowMode === "cash" ? "bg-white text-neutral-700 shadow-sm" : "text-neutral-400 hover:text-neutral-600"}`}
                  aria-pressed={cashflowMode === "cash"}
                >
                  Gross
                </button>
                <button
                  onClick={() => setCashflowMode("tax")}
                  className={`px-1.5 py-0.5 rounded-full transition ${cashflowMode === "tax" ? "bg-white text-neutral-700 shadow-sm" : "text-neutral-400 hover:text-neutral-600"}`}
                  aria-pressed={cashflowMode === "tax"}
                >
                  Loss
                </button>
              </div>
              <div className={`mt-1 text-lg font-semibold ${displayAnnual >= 0 ? "text-emerald-700" : "text-red-500"}`}>
                {displayAnnual >= 0 ? "+" : ""}{currency(Math.round(displayAnnual / 12))}/mo
              </div>
              <div className="text-xs text-neutral-400 mt-0.5">
                {currency(Math.round(Math.abs(displayAnnual)))}/yr {displayAnnual >= 0 ? "positive" : "shortfall"}
              </div>
            </div>
          );
        })()}
        <MetricCard
          label="Gross Yield"
          value={portfolioGrossYield === 0 ? "—" : `${portfolioGrossYield.toFixed(1)}%`}
          valueClass={
            portfolioGrossYield === 0
              ? "text-neutral-900"
              : portfolioGrossYield >= 5
              ? "text-emerald-600"
              : portfolioGrossYield >= 3
              ? "text-amber-600"
              : "text-red-600"
          }
          sub={totalAnnualRent > 0
            ? `${totalAnnualRent >= 1_000_000
                ? "$" + (totalAnnualRent / 1_000_000).toFixed(1) + "m"
                : "$" + Math.round(totalAnnualRent / 1_000) + "k"}/yr`
            : undefined}
          tip={`Gross yield = total annual rent ÷ total portfolio value. Net of ${
            totalAnnualRent >= 1_000_000
              ? "$" + (totalAnnualRent / 1_000_000).toFixed(1) + "m"
              : "$" + Math.round(totalAnnualRent / 1_000) + "k"
          } annual rent across ${activeProperties.length} properties.`}
        />
        {/* B103 Phase 4c-1 — single-adult: existing card unchanged.
            Multi-adult: stacked layout with household total + per-adult
            sub-cards in the same grid slot. */}
        {adults.length === 1 ? (
          <MetricCard
            label="Est. Tax Saving"
            value={
              !hasIncome
                ? "Set income"
                : isPositivelyGeared
                ? "Positively geared ✓"
                : negGearingLoss > 0
                ? `+${currency(Math.round(taxSaving))}/yr`
                : "—"
            }
            valueClass={
              !hasIncome
                ? "text-neutral-400"
                : negGearingLoss > 0 || isPositivelyGeared
                ? "text-emerald-600"
                : "text-neutral-400"
            }
            sub={
              !hasIncome
                ? "needed for calculation"
                : negGearingLoss > 0
                ? `at ${marginalRate}% marginal rate`
                : isPositivelyGeared
                ? "all properties profitable"
                : undefined
            }
            tip="Estimated annual tax saving from negative gearing. Based on your marginal rate and net rental losses across personally-held properties. Trusts and companies quarantine losses — those are excluded. Not financial advice."
          />
        ) : (
          <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm border-t-2 border-t-[#C47A2F]">
            {/* Household total */}
            <div className="flex items-center gap-1 text-xs font-medium tracking-wide uppercase text-neutral-500 mb-1.5">
              Est. Tax Saving (household)
              <InfoTooltip tip="Combined personal tax saving across all adults. Trust / SMSF / Company losses are quarantined and excluded per ATO rules (see the Trusts / SMSF branches on the Tax page for entity-level positions)." />
            </div>
            <div className={`text-lg font-semibold ${
              householdAllocatedLoss > 0 || isPositivelyGeared
                ? "text-emerald-600"
                : "text-neutral-400"
            }`}>
              {householdAllocatedLoss > 0
                ? `+${currency(Math.round(householdTotalTaxSaving))}/yr`
                : isPositivelyGeared
                ? "Positively geared ✓"
                : "—"}
            </div>
            {/* Per-adult sub-cards */}
            <div className="mt-3 space-y-2 border-t border-neutral-100 pt-2">
              {perAdultTax.map((adult) => (
                <div key={adult.id} className="text-xs">
                  <div className="flex items-center gap-1 font-medium text-neutral-700">
                    {adult.label}
                    <InfoTooltip tip={`Allocated share of personal-name rental losses based on ownership mode (Sole / JT / TIC). Saved at this adult's effective marginal rate${adult.componentsLabel ? ` (bracket + ${adult.componentsLabel})` : ""}.`} />
                  </div>
                  {adult.allocatedLoss > 0 ? (
                    <>
                      <div className={`mt-0.5 font-semibold ${adult.taxSaving > 0 ? "text-emerald-600" : "text-neutral-700"}`}>
                        +{currency(Math.round(adult.taxSaving))}/yr
                      </div>
                      <div className="text-neutral-400">
                        on {currency(Math.round(adult.allocatedLoss))} loss · at {adult.bracketRate}% marginal rate
                      </div>
                    </>
                  ) : (
                    <div className="mt-0.5 text-neutral-400 italic">No allocated rental loss this FY.</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <MetricCard
          label="Tax This FY"
          value={
            !hasIncome
              ? "Set income"
              : paygWithheld > 0
              ? netOwing > 0
                ? `${currency(Math.round(netOwing))} owing`
                : `${currency(Math.round(Math.abs(netOwing)))} refund`
              : `${currency(Math.round(estimatedTaxWithProperty))}`
          }
          valueClass={
            !hasIncome
              ? "text-neutral-400"
              : paygWithheld > 0 && netOwing <= 0
              ? "text-emerald-600"
              : "text-neutral-900"
          }
          sub={
            !hasIncome
              ? "needed for estimate"
              : paygWithheld > 0
              ? `est. tax ${currency(Math.round(estimatedTaxWithProperty))} · withheld ${currency(Math.round(paygWithheld))}`
              : taxReduction > 0
              ? `saves ${currency(Math.round(taxReduction))} vs salary alone`
              : `on ${currency(Math.round(taxableIncome))} taxable income`
          }
          tip="Estimated annual income tax including Medicare levy and LITO (ATO 2024-25). If PAYG withheld is set in My Profile, shows est. refund or amount owing. Includes neg. gearing offset for personal-name properties. Not tax advice."
        />
      </div>

      {/* SMSF summary line — only shown when SMSF entity has properties */}
      {smsfActive.length > 0 && (() => {
        // Use propertyMetrics — canonical cashflow source (ARCHITECTURE §7).
        // netCashflowShare is already ownership-adjusted — no extra factor.
        const smsfAnnualCashflow = smsfActive.reduce(
          (s, p) => s + propertyMetrics(p).netCashflowShare * 12,
          0
        );
        const smsfContributions = portfolioStructures
          .filter((s) => s.entityType === "SMSF")
          .reduce((s, st) => s + (st.monthlyContributions ?? 0), 0);
        const smsfNetMonthly = smsfAnnualCashflow / 12 + smsfContributions;
        return (
          <div className="rounded-xl bg-purple-50 border border-purple-200 px-4 py-2.5 flex items-center gap-3">
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">SMSF</span>
            <span className="text-sm text-purple-900">
              Net fund position: <strong className={smsfNetMonthly >= 0 ? "text-emerald-700" : "text-red-600"}>
                {smsfNetMonthly >= 0 ? "+" : ""}{currency(Math.round(smsfNetMonthly))}/mo
              </strong>
              <span className="text-xs text-purple-700 ml-2">
                ({currency(Math.round(smsfAnnualCashflow / 12))}/mo cashflow + {currency(Math.round(smsfContributions))}/mo contributions)
              </span>
            </span>
          </div>
        );
      })()}

      {/* Setup Checklist */}
      {setupComplete && !setupDismissed && !Object.values(setupComplete).every(Boolean) && (
        <div className="rounded-xl border border-[#C47A2F]/30 bg-[#FDF3E7] p-4 h-auto overflow-visible">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[#C47A2F]">Complete your setup</h2>
            <button
              onClick={() => {
                localStorage.setItem("ppa:setup-dismissed", "true");
                setSetupDismissed(true);
              }}
              className="text-neutral-400 hover:text-neutral-600 text-xs"
            >
              Dismiss
            </button>
          </div>
          <div className="flex flex-col gap-2 min-h-0">
            {[
              { key: "profile" as const, label: "Set your income in My Profile", page: "profile" },
              { key: "portfolio" as const, label: "Add your first property", page: "portfolio" },
              { key: "loans" as const, label: "Add loan details", page: "loans" },
              { key: "budget" as const, label: "Set up your personal budget", page: "budget" },
              { key: "goals" as const, label: "Set your investment goal", page: "strategy" },
            ].map(({ key, label, page }) => (
              <button
                key={key}
                onClick={() => !setupComplete[key] && onNavigate(page)}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm transition-colors ${
                  setupComplete[key]
                    ? "text-neutral-400 cursor-default"
                    : "text-neutral-700 hover:bg-[#C47A2F]/10 cursor-pointer"
                }`}
              >
                <span className={`flex-shrink-0 w-4 h-4 rounded-full border flex items-center justify-center ${
                  setupComplete[key]
                    ? "bg-emerald-500 border-emerald-500 text-white"
                    : "border-[#C47A2F]"
                }`}>
                  {setupComplete[key] && (
                    <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                      <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
                <span className={setupComplete[key] ? "line-through" : ""}>{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Referral widget — investor tier only */}
      {subscriptionTier === "investor" && referralCode && !referralDismissed && (
        <div className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold text-neutral-900">Refer a friend, get 1 month free</h2>
              <p className="mt-0.5 text-xs text-neutral-500">
                Share your unique link. When they subscribe, you both win.
              </p>
            </div>
            <button
              onClick={() => {
                localStorage.setItem("ausprop_referral_dismissed", "true");
                setReferralDismissed(true);
              }}
              className="shrink-0 text-neutral-300 hover:text-neutral-500 transition-colors text-xs mt-0.5"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2 text-xs font-mono text-neutral-700 truncate">
              ausprop.app?ref={referralCode}
            </div>
            <button
              onClick={() => {
                void navigator.clipboard.writeText(`https://ausprop.app?ref=${referralCode}`);
                setReferralCopied(true);
                setTimeout(() => setReferralCopied(false), 2000);
              }}
              className="shrink-0 rounded-lg bg-neutral-900 px-3 py-2 text-xs font-medium text-white hover:bg-neutral-700 transition-colors"
            >
              {referralCopied ? "Copied ✓" : "Copy"}
            </button>
          </div>

          {referralStats.total > 0 && (
            <p className="text-xs text-neutral-400">
              {referralStats.total} {referralStats.total === 1 ? "friend" : "friends"} referred
              {referralStats.subscribed > 0 && ` · ${referralStats.subscribed} subscribed`}
            </p>
          )}
        </div>
      )}

      {/* Notice Board (N5 Stage 2 — replaces Priority Actions) */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-neutral-900">Notice Board</h2>
          {noticeBoard.length > 0 && <span className="text-xs text-neutral-400">{noticeBoard.length} item{noticeBoard.length !== 1 ? "s" : ""}</span>}
        </div>
        {noticeBoard.length === 0 ? (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">
            ✓ All clear — no upcoming reminders.
          </div>
        ) : (
          <div className="space-y-4">
            {redItems.length > 0 && <NoticeTier label="Urgent" items={redItems} colour="red" onNavigate={onNavigate} onEmail={setEmailItem} onSelectProperty={onSelectProperty} />}
            {amberItems.length > 0 && <NoticeTier label="This month" items={amberItems} colour="amber" onNavigate={onNavigate} onEmail={setEmailItem} onSelectProperty={onSelectProperty} />}
            {greenItems.length > 0 && <NoticeTier label="Opportunities" items={greenItems} colour="green" onNavigate={onNavigate} onEmail={setEmailItem} onSelectProperty={onSelectProperty} />}
            {neutralItems.length > 0 && <NoticeTier label="Notes" items={neutralItems} colour="neutral" onNavigate={onNavigate} onEmail={setEmailItem} onSelectProperty={onSelectProperty} />}
          </div>
        )}
      </section>

      {/* Borrowing Capacity Reset Flag */}
      {entityGroups
        .filter((eg) => eg.cashflow > 0 && eg.count > 0)
        .map((eg) => (
          <div
            key={eg.id}
            className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-xs flex items-center justify-center font-bold shrink-0">
                ↑
              </div>
              <div>
                <p className="text-sm font-semibold text-emerald-800">
                  {eg.name} is cashflow positive
                </p>
                <p className="text-sm text-emerald-700 mt-0.5">
                  This may support a borrowing capacity reset with your lender. Speak with your mortgage broker.
                </p>
                <p className="text-xs text-neutral-400 mt-1.5">
                  This is not financial advice. Consult your mortgage broker and accountant.
                </p>
              </div>
            </div>
          </div>
        ))}

      {/* Properties — full-width table with HTAG market signals */}
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-neutral-800">Properties</h2>
          <div className="flex items-center gap-3">
            {htagCache.size > 0 && (
              <span className="text-[10px] text-neutral-300">Market signals powered by HtAG · Updated monthly</span>
            )}
            <button onClick={() => onNavigate("portfolio")} className="text-xs text-neutral-400 hover:text-neutral-700">View all →</button>
          </div>
        </div>
        {sortedProperties.length === 0 ? (
          <div className="text-sm text-neutral-400 py-6 text-center">
            No properties yet.{" "}
            <button onClick={() => onNavigate("portfolio")} className="underline hover:text-neutral-700">Add one</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-neutral-400 border-b border-neutral-200">
                  <th className="text-left py-2 pr-2 font-medium">Property</th>
                  <th className="text-left py-2 px-2 font-medium">Suburb</th>
                  <th className="text-right py-2 px-2 font-medium">Value</th>
                  <th className="text-right py-2 px-2 font-medium">Cashflow</th>
                  <th className="text-center py-2 px-2 font-medium hidden lg:table-cell">Signal</th>
                  <th className="text-center py-2 px-2 font-medium hidden lg:table-cell">Cycle</th>
                  <th className="text-right py-2 px-2 font-medium hidden lg:table-cell">1yr Growth</th>
                  <th className="text-right py-2 px-2 font-medium hidden lg:table-cell">Vacancy</th>
                  <th className="py-2 pl-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {sortedProperties.map((prop) => {
                  const pm = propertyMetrics(prop);
                  const propCashflow = pm.netCashflow * 12;
                  const htagType = getHtagType(prop.propertyType ?? prop.assetClass ?? "") ?? "house";
                  const htag = prop.lgaId ? htagCache.get(`${prop.lgaId}:${htagType}`) : undefined;
                  const isInvestor = subscriptionTier === "investor";

                  // Derive signal from cycle + scores
                  const signal = htag && isInvestor ? (() => {
                    const phase = (htag.cycle?.raw_phase ?? htag.cycle?.phase ?? "").toLowerCase();
                    const rcs = htag.scores?.overall ?? 0;
                    if (phase.includes("falling") || (phase.includes("peak") && phase.includes("-")))
                      return { label: "SELL", color: "red" as const };
                    if (phase.includes("bottom") || phase.includes("rising"))
                      return rcs >= 55 ? { label: "ACCUM", color: "green" as const } : { label: "HOLD", color: "green" as const };
                    if (phase.includes("peak"))
                      return { label: "WATCH", color: "amber" as const };
                    return { label: "HOLD", color: "green" as const };
                  })() : null;

                  // Cycle short label
                  const cycleLabel = htag && isInvestor ? (() => {
                    const p = htag.cycle?.phase ?? "";
                    if (p === "(+) Peak") return { text: "Peak \u2191", cls: "text-amber-600" };
                    if (p === "(-) Peak") return { text: "Peak \u2193", cls: "text-amber-600" };
                    if (p === "Rising") return { text: "Rising", cls: "text-emerald-600" };
                    if (p === "Bottom") return { text: "Bottom", cls: "text-emerald-600" };
                    if (p === "Falling") return { text: "Falling", cls: "text-red-600" };
                    return { text: p || "\u2014", cls: "text-neutral-500" };
                  })() : null;

                  const dotColor = signal?.color === "green" ? "bg-emerald-500" : signal?.color === "amber" ? "bg-amber-500" : signal?.color === "red" ? "bg-red-500" : "";
                  const signalTextColor = signal?.color === "green" ? "text-emerald-700" : signal?.color === "amber" ? "text-amber-700" : "text-red-700";

                  return (
                    <tr
                      key={prop.id}
                      className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50 cursor-pointer"
                      onClick={() => { if (onSelectProperty) onSelectProperty(prop.id); onNavigate("property-detail"); }}
                    >
                      <td className="py-2.5 pr-2">
                        <div className="font-medium text-neutral-800 truncate max-w-[160px]">{prop.nickname || prop.address}</div>
                      </td>
                      <td className="py-2.5 px-2 text-neutral-500 truncate max-w-[100px]">{prop.suburb}</td>
                      <td className="py-2.5 px-2 text-right font-medium text-neutral-800">{currency(prop.currentValue ?? 0)}</td>
                      <td className={`py-2.5 px-2 text-right ${propCashflow >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                        {propCashflow >= 0 ? "+" : ""}{currency(Math.round(propCashflow / 12))}/mo
                      </td>
                      <td className="py-2.5 px-2 text-center hidden lg:table-cell">
                        {!isInvestor ? (
                          <span className="text-neutral-300" title="Available on Investor plan">🔒</span>
                        ) : signal ? (
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold ${signalTextColor}`}>
                            <span className={`w-2 h-2 rounded-full ${dotColor}`} />
                            {signal.label}
                          </span>
                        ) : <span className="text-neutral-300">—</span>}
                      </td>
                      <td className="py-2.5 px-2 text-center hidden lg:table-cell">
                        {!isInvestor ? (
                          <span className="text-neutral-300" title="Available on Investor plan">🔒</span>
                        ) : cycleLabel ? (
                          <span className={`text-xs font-medium ${cycleLabel.cls}`}>{cycleLabel.text}</span>
                        ) : <span className="text-neutral-300">—</span>}
                      </td>
                      <td className="py-2.5 px-2 text-right hidden lg:table-cell">
                        {!isInvestor ? (
                          <span className="text-neutral-300" title="Available on Investor plan">🔒</span>
                        ) : htag ? (
                          <span className={`text-xs font-medium ${(htag.growth?.price_1y ?? 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                            {(htag.growth?.price_1y ?? 0) >= 0 ? "+" : ""}{((htag.growth?.price_1y ?? 0) * 100).toFixed(1)}%
                          </span>
                        ) : <span className="text-neutral-300">—</span>}
                      </td>
                      <td className="py-2.5 px-2 text-right hidden lg:table-cell">
                        {!isInvestor ? (
                          <span className="text-neutral-300" title="Available on Investor plan">🔒</span>
                        ) : htag ? (() => {
                          const vr = htag.demand?.vacancy_rate ?? -1;
                          if (vr < 0) return <span className="text-neutral-300">—</span>;
                          const cls = vr < 0.02 ? "text-emerald-600" : vr <= 0.035 ? "text-neutral-600" : "text-amber-600";
                          return <span className={`text-xs font-medium ${cls}`}>{(vr * 100).toFixed(1)}%</span>;
                        })() : <span className="text-neutral-300">—</span>}
                      </td>
                      <td className="py-2.5 pl-2 text-right">
                        <span className="text-xs text-neutral-400 hover:text-neutral-700">View →</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Goal Progress — full-width */}
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-neutral-800 mb-4">
            Goal Progress
          </h2>

          {passiveTarget > 0 ? (
            <div className="space-y-4">
              {/* Circular progress ring */}
              <div className="flex items-center gap-5">
                <div className="shrink-0">
                  <svg
                    viewBox="0 0 100 100"
                    width="96"
                    height="96"
                    className="block"
                  >
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="#e5e7eb"
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="#10b981"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray="251.2"
                      strokeDashoffset={251.2 * (1 - passivePct / 100)}
                      strokeLinecap="round"
                      transform="rotate(-90 50 50)"
                    />
                    <text
                      x="50"
                      y="54"
                      textAnchor="middle"
                      fontSize="18"
                      fontWeight="700"
                      fill="#111827"
                    >
                      {Math.round(passivePct)}%
                    </text>
                  </svg>
                </div>
                <div className="space-y-1">
                  <div className="text-xs text-neutral-400">
                    Passive income target
                  </div>
                  <div className="text-lg font-semibold text-neutral-900">
                    {currency(passiveTarget)}/yr
                  </div>
                  <div
                    className={`text-sm font-medium ${
                      currentNet >= 0 ? "text-emerald-700" : "text-red-500"
                    }`}
                  >
                    Current:{" "}
                    {currentNet >= 0 ? "+" : ""}
                    {currency(Math.round(currentNet))}/yr
                  </div>
                  {yearsToTarget === 0 && (
                    <div className="text-xs text-emerald-600 font-medium">
                      Target reached
                    </div>
                  )}
                  {yearsToTarget !== null && yearsToTarget > 0 && (
                    <div className="text-xs text-neutral-500">
                      Est. {yearsToTarget} yr{yearsToTarget === 1 ? "" : "s"} at
                      4% rent growth
                    </div>
                  )}
                  {yearsToTarget === null && (
                    <div className="text-xs text-neutral-400">
                      Projected &gt;30 yrs at current growth
                    </div>
                  )}
                </div>
              </div>

              {/* Additional goal stats */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-neutral-100">
                <div>
                  <div className="text-xs text-neutral-400">Annual rent</div>
                  <div className="text-sm font-semibold text-neutral-800">
                    {currency(Math.round(totalAnnualRent))}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-neutral-400">
                    Annual expenses
                  </div>
                  <div className="text-sm font-semibold text-neutral-800">
                    {currency(Math.round(totalAnnualExpenses + totalAnnualInterest))}
                  </div>
                </div>
                {profile?.portfolioValueTarget && (
                  <>
                    <div>
                      <div className="text-xs text-neutral-400">
                        Portfolio value target
                      </div>
                      <div className="text-sm font-semibold text-neutral-800">
                        {currency(profile.portfolioValueTarget)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-neutral-400">
                        Current value
                      </div>
                      <div className="text-sm font-semibold text-neutral-800">
                        {currency(totalValue)}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-neutral-400 py-6 text-center">
              Set a passive income target in your{" "}
              <button
                onClick={() => onNavigate("profile")}
                className="underline hover:text-neutral-700"
              >
                profile
              </button>{" "}
              to track goal progress.
            </div>
          )}
        </div>

      {/* Personal Finances */}
      <div>
        <button
          onClick={() => setShowFinances((v) => !v)}
          className="w-full flex items-center justify-between py-3 px-4 bg-white rounded-xl border border-neutral-200 hover:bg-neutral-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-neutral-800">Personal Finances</span>
            {hasBudgetData && (
              <span
                className={`text-xs font-medium ${
                  netPersonalPosition >= 0 ? "text-emerald-600" : "text-red-500"
                }`}
              >
                Net position: {currency(Math.round(netPersonalPosition))}/mo after all costs
                {hasActuals && (
                  <span className={`ml-2 ${isOverBudget ? "text-red-500" : "text-emerald-600"}`}>
                    · {currency(varianceAmt)} {isOverBudget ? "over" : "under"} budget
                  </span>
                )}
              </span>
            )}
          </div>
          <span className="text-neutral-400 text-xs">{showFinances ? "▲" : "▼"}</span>
        </button>

        {showFinances && (
          <div className="mt-3 rounded-xl border border-neutral-200 bg-white p-5">
            {!hasBudgetData ? (
              <div className="text-center py-6 text-sm text-neutral-500">
                <p className="mb-2">Complete your Personal Budget to see finances here.</p>
                <button
                  onClick={() => onNavigate("budget")}
                  className="text-sm font-medium text-neutral-900 hover:underline"
                >
                  Set up budget →
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: Budget summary */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-neutral-700">Budget Summary</span>
                    <button
                      onClick={() => onNavigate("budget")}
                      className="text-xs text-neutral-400 hover:text-neutral-700"
                    >
                      View full budget →
                    </button>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-neutral-500">Monthly income</span>
                      <span className="font-medium text-neutral-800">
                        {currency(Math.round(monthlyIncome))}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-500">Monthly expenses</span>
                      <span className="font-medium text-neutral-800">
                        −{currency(Math.round(monthlyExpenses))}
                      </span>
                    </div>
                    {monthlyShortfall > 0 && (
                      <div className="flex justify-between">
                        <span className="text-neutral-500">Portfolio top-up</span>
                        <span className="font-medium text-red-600">
                          −{currency(Math.round(monthlyShortfall))}
                        </span>
                      </div>
                    )}
                    <div className="border-t border-neutral-100 pt-1.5 flex justify-between font-semibold">
                      <span className="text-neutral-700">Net position</span>
                      <span
                        className={
                          netPersonalPosition >= 0 ? "text-emerald-700" : "text-red-600"
                        }
                      >
                        {netPersonalPosition >= 0 ? "" : "−"}
                        {currency(Math.abs(Math.round(netPersonalPosition)))}/mo
                      </span>
                    </div>
                  </div>
                  {netPersonalPosition < 0 ? (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                      Budget deficit after property costs — review expenses or increase income.
                    </div>
                  ) : (
                    <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                      {currency(Math.round(netPersonalPosition))}/mo available after all costs.
                    </div>
                  )}
                </div>

                {/* Right: Bucket status */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-neutral-700">Savings Buckets</span>
                    {buckets.length > 0 && (
                      <span className="text-xs text-neutral-400">
                        {currency(Math.round(totalBucketAllocation))}/mo allocated
                      </span>
                    )}
                  </div>
                  {buckets.length === 0 ? (
                    <div className="text-sm text-neutral-400 py-4 text-center">
                      No buckets set up.{" "}
                      <button
                        onClick={() => onNavigate("budget")}
                        className="underline hover:text-neutral-700"
                      >
                        Add buckets
                      </button>{" "}
                      in your budget.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {buckets.map((bucket) => {
                        const pct =
                          personalSurplus > 0
                            ? Math.min(100, ((bucket.monthlyAmount ?? 0) / personalSurplus) * 100)
                            : 0;
                        return (
                          <div key={bucket.id}>
                            <div className="flex justify-between text-xs mb-0.5">
                              <span className="text-neutral-700 font-medium">{bucket.name}</span>
                              <span className="text-neutral-500">
                                {currency(Math.round(bucket.monthlyAmount ?? 0))}/mo
                              </span>
                            </div>
                            <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                              <div
                                className="h-full rounded-full bg-neutral-700"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                      {overAllocated && (
                        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                          Bucket allocations exceed budget surplus — review in Personal Budget.
                        </div>
                      )}
                      {budgetSettings?.spareIncomeAvailableForInvesting != null && (
                        <div className="mt-2 pt-2 border-t border-neutral-100 flex justify-between text-xs">
                          <span className="text-neutral-500">Spare for investing</span>
                          <span className="font-medium text-emerald-700">
                            {currency(Math.round(budgetSettings.spareIncomeAvailableForInvesting))}/mo
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Entity Performance */}
      {(entityGroups.length > 0 || unassignedProps.length > 0) && (
        <div>
          <h2 className="text-base font-semibold text-neutral-800 mb-3">
            Entity Performance
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {entityGroups
              .filter((g) => g.count > 0)
              .map((g) => (
                <div
                  key={g.id}
                  className="rounded-xl border border-neutral-200 bg-white p-4"
                >
                  <div className="text-xs text-neutral-400 mb-1">
                    {g.type}
                  </div>
                  <div className="text-sm font-semibold text-neutral-800 truncate mb-1">
                    {g.name}
                  </div>
                  <div
                    className={`text-lg font-semibold ${
                      g.cashflow >= 0 ? "text-emerald-700" : "text-red-500"
                    }`}
                  >
                    {g.cashflow >= 0 ? "+" : ""}
                    {currency(Math.round(g.cashflow))}/yr
                  </div>
                  {isCompanyEntity(g.type) && g.cashflow > 0 && (
                    <div className="text-xs text-neutral-400 mt-0.5">after 30% company tax</div>
                  )}
                  <div className="text-xs text-neutral-400 mt-0.5">
                    {g.count} {g.count === 1 ? "property" : "properties"}
                  </div>
                </div>
              ))}
            {unassignedProps.length > 0 && (
              <div className="rounded-xl border border-neutral-200 bg-white p-4">
                <div className="text-xs text-neutral-400 mb-1">Personal</div>
                <div className="text-sm font-semibold text-neutral-800 mb-1">
                  Held in personal name(s)
                </div>
                <div
                  className={`text-lg font-semibold ${
                    unassignedCashflow >= 0
                      ? "text-emerald-700"
                      : "text-red-500"
                  }`}
                >
                  {unassignedCashflow >= 0 ? "+" : ""}
                  {currency(Math.round(unassignedCashflow))}/yr
                </div>
                <div className="text-xs text-neutral-400 mt-0.5">
                  {unassignedProps.length}{" "}
                  {unassignedProps.length === 1 ? "property" : "properties"}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty state */}
      {activeProperties.length === 0 && (
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-8 text-center">
          <div className="text-neutral-500 text-sm mb-3">
            No properties in your portfolio yet.
          </div>
          <button
            onClick={() => onNavigate("portfolio")}
            className="text-sm font-medium text-neutral-800 underline hover:text-neutral-600"
          >
            Go to Portfolio to add your first property
          </button>
        </div>
      )}

      {/* Email compose modal (N5 Stage 5) */}
      {emailItem && (
        <EmailComposeModal
          item={emailItem}
          property={activeProperties.find((p) => p.id === emailItem.propertyId)}
          profile={profile}
          teamContacts={profile.teamContacts ?? []}
          onClose={() => setEmailItem(null)}
        />
      )}
    </div>
  );
}
